<template>
  <v-card class="machines-chips-card" elevation="1" style="border-radius: 12px;">
    <v-card-title class="pb-1 d-flex align-center">
      <span class="text-h6">Machines</span>
    </v-card-title>

    <v-card-text class="pt-0">
      <div class="machines-summary list-header d-flex text-caption text--secondary px-2">
        <div class="col-host">Hôte</div>
        <div class="col-workers text-right">Workers</div>
        <div class="col-state text-right">État</div>
      </div>

      <!-- Summary meta above list -->
      <div class="summary-meta d-flex px-2 py-1 text-caption">
        <div class="mr-4">Actives: <strong>{{ onlineMachines.length }}</strong></div>
        <div class="mr-4">Total workers: <strong>{{ totalWorkers }}</strong></div>
      </div>

      <div class="machines-container list-body">
        <div
          v-for="m in sortedMachines"
          :key="m.host"
          class="row-item d-flex align-center"
          @click="$emit('machine-click', m)"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <div class="col-host text-truncate" v-bind="attrs" v-on="on">
                <span :class="['state-dot', m.status]"></span>{{ m.host }}
              </div>
            </template>
            <span>{{ m.host }}</span>
          </v-tooltip>

          <div class="col-workers text-right tabular">{{ m.workers }} / {{ m.totalWorkers || m.workers }}</div>

          <div class="col-state text-right">
            <v-chip x-small :color="m.status === 'online' ? 'success' : 'error'" text-color="white">
              {{ m.status === 'online' ? 'En ligne' : 'Hors ligne' }}
            </v-chip>
          </div>
        </div>

        <div v-if="machines.length === 0" class="empty-state text-center py-4">Aucune machine</div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'MachinesChipsCard',
  props: {
    machines: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    onlineMachines() {
      return this.machines.filter(m => m.status === 'online');
    },
    offlineMachines() {
      return this.machines.filter(m => m.status === 'offline');
    },
    totalWorkers() {
      return this.machines.reduce((sum, machine) => sum + (machine.totalWorkers || machine.workers || 0), 0);
    },
    // averageLoad retained if needed elsewhere; not used in UI after removing Charge
    averageLoad() {
      if (this.onlineMachines.length === 0) return 0;
      const totalLoad = this.onlineMachines.reduce((sum, machine) => {
        return sum + (((machine.cpu || 0) + (machine.memory || 0)) / 2);
      }, 0);
      return totalLoad / this.onlineMachines.length;
    },
    sortedMachines() {
      const list = this.machines.map(m => ({ ...m }));
      return list.sort((a, b) => {
        if (a.status !== b.status) return a.status === 'online' ? -1 : 1;
        const aw = (a.workers || a.totalWorkers || 0);
        const bw = (b.workers || b.totalWorkers || 0);
        return bw - aw; // more workers first
      });
    }
  },
  methods: {
    getMachineColor(machine) {
      if (machine.status === 'offline') return 'error';
      if (machine.workers === 0) return 'grey';
      if (machine.cpu > 80 || machine.memory > 80) return 'warning';
      return 'primary';
    },
    getMachineClass(machine) {
      return {
        'machine-offline': machine.status === 'offline',
        'machine-overloaded': machine.cpu > 80 || machine.memory > 80,
        'machine-clickable': true
      };
    },
    getMachineIcon(machine) {
      if (machine.status === 'offline') return 'mdi-server-off';
      if (machine.workers === 0) return 'mdi-server-minus';
      return 'mdi-server';
    },
    getWorkerCountColor(workerCount) {
      if (workerCount === 0) return 'grey darken-1';
      if (workerCount > 5) return 'success darken-1';
      return 'info darken-1';
    },
    getMachineStatusColor(machine) {
      if (machine.status === 'offline') return 'white';
      if (machine.cpu > 90 || machine.memory > 90) return 'red';
      if (machine.cpu > 70 || machine.memory > 70) return 'orange';
      return 'white';
    },
    getMachineStatusIcon(machine) {
      if (machine.status === 'offline') return 'mdi-close-circle';
      if (machine.cpu > 80 || machine.memory > 80) return 'mdi-alert-circle';
      return 'mdi-check-circle';
    },
    getMachineStatusText(machine) {
      if (machine.status === 'offline') {
        return 'Machine hors ligne';
      }
      if (machine.cpu > 80 || machine.memory > 80) {
        return 'Machine surchargée';
      }
      return 'Machine en bonne santé';
    },
    formatLastSeen(timestamp) {
      if (!timestamp) return '—';

      const now = new Date();
      const lastSeen = new Date(timestamp);
      const diffMs = now - lastSeen;
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMins / 60);

      if (diffMins < 1) return 'À l\'instant';
      if (diffMins < 60) return `Il y a ${diffMins}m`;
      if (diffHours < 24) return `Il y a ${diffHours}h`;
      return lastSeen.toLocaleDateString();
    }
  }
};
</script>

<style lang="scss" scoped>
.machines-chips-card {
  height: 100%;
  display: flex;
  flex-direction: column;

  .v-card-text {
    flex: 1;
    overflow-y: auto;
    padding-top: 6px !important;
  }
}

.list-header {
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
  padding: 4px 6px;
  border-bottom: 1px solid #eee;
}

.list-body { overflow-y: auto; }
.row-item { height: 30px; padding: 0 6px; font-size: 0.85rem; border-bottom: 1px dashed #f0f0f0; }

.col-host { flex: 1 1 50%; min-width: 0; }
.col-workers { width: 30%; }
.col-state { width: 20%; }

.state-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; vertical-align: middle; }
.state-dot.online { background: #10B981; }
.state-dot.offline { background: #EF4444; }

.summary-meta { border-bottom: 1px solid #f1f1f1; }

.tabular { font-variant-numeric: tabular-nums; }

.empty-state { color: #6b7280; }

@media (max-width: 768px) {
  .machines-chips-card { height: 100%; }
}
</style>